import React, { useContext, useEffect, useState } from 'react'
import { Navigate, useLocation, useSearchParams } from 'react-router-dom'
import { useForm, Controller } from 'react-hook-form'

// Components
import { Alert } from '../../components/Alert'
import { Button } from '../../components/Button'
import { AuthGradient } from '../../components/AuthGradient'
import { PasswordInput } from '../../components/PasswordInput'
import { TextInput } from '../../components/TextInput'

// Images
import Badger from '../../assets/images/badgr_text_logo.svg'
import EventstackLogo from '../../assets/images/eventstack_logo.svg'
import Fetch from '../../assets/images/fetch_text_logo.svg'
import Shield from '../../assets/images/shield_security.svg'
import SMS from '../../assets/images/sms.svg'

// Store
import { UserStoreContext } from '../../stores/UserStore'

// Utils and messages
import useFlashMessage from '../../hooks/FlashMessage'
import { verifyPassword } from '../../utils/helpers'

// Service
import { resetPassword, validateResetToken } from '../../services/user.service'

/**
 *
 * PasswordReset
 *
 */
const PasswordReset = () => {
  // Context
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const { getUpdatedUser, isAuthenticated, setCurrentTokens } = useContext(UserStoreContext)

  const token = searchParams.get('token')
  const redirectTo = searchParams.get('redirect')
  const email = searchParams.get('email')
  const newUser = location.pathname === '/set-password'

  // State
  const [loading, setLoading] = useState(false)
  const [redirect, setRedirect] = useState(false)
  const [staticError, setStaticError] = useState(null)

  // Messages
  const { message: error, showMessage: showError, clearMessage: clearError } = useFlashMessage(null)

  useEffect(() => {
    if (redirectTo === 'fetch' && !isAuthenticated) {
      let deepLink = `password-reset?token=${token}`
      if (newUser) deepLink = deepLink.concat(`&email=${email}`)
      window.location.replace(`${import.meta.env.VITE_FETCH_DEEP_LINK_SCHEME}://${deepLink}`)
    }

    if (token && !isAuthenticated) {
      validateResetToken(token, email || null, (err) => {
        if (err.includes('expired') && newUser) {
          setStaticError(err)
        } else if (err.includes('expired')) {
          setRedirect('/password-reset-request')
        } else {
          showError(err)
        }
      })
    }
  }, [])

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  })

  /**
   * Helper to set the redirect parameters based on whether or not the user is
   * setting or RE-setting a password
   */
  const getRedirectParams = () => {
    if (newUser) return '?created=true'
    if (redirect === '/password-reset-request') return '?tokenExpired=true'
    return '?reset=true'
  }

  /**
   * Handles form submission
   * - Logs the user in if successful (via setting tokens in the store)
   * @param {object} data
   */
  const onSubmit = async (data) => {
    await resetPassword(
      { ...data, token },
      showError,
      setLoading,
      setCurrentTokens,
      getUpdatedUser,
    )
  }

  return (
    <>
      {redirect && <Navigate to={{ pathname: '/login', search: getRedirectParams() }} />}

      <div className="relative flex h-full w-full flex-col items-center justify-between overflow-hidden bg-[#181823] py-12 text-white">
        <AuthGradient />

        <div className="w-64 self-center sm:ml-8 sm:w-[184px] sm:self-start">
          <img src={EventstackLogo} alt="" />
        </div>

        <div className="z-1 relative mt-12 h-full max-w-[675px] sm:mx-auto sm:mt-0 sm:h-auto sm:w-2/3">
          <h2 className="text-center text-3xl font-bold">
            {newUser ? 'Set your password' : 'Reset your password'}
          </h2>

          <p className="mb-4 mt-2 text-center text-sm text-white">
            Or{' '}
            <a
              href={`/login${redirectTo ? `?redirect=${redirectTo}` : ''}`}
              className="font-medium text-purple hover:text-purple"
            >
              Log In
            </a>
          </p>

          {/* Status Messages */}
          {(error || staticError) && (
            <Alert
              className="mb-6"
              message={error || staticError}
              onClose={() => {
                clearError()
                setStaticError(null)
              }}
              title="Error"
              type="error"
            />
          )}

          {!staticError && (
            <form
              className="mt-8 space-y-8 sm:mt-0 sm:space-y-6"
              id="password-reset-form"
              onSubmit={handleSubmit(onSubmit)}
            >
              <div className="space-y-2 text-center">
                {newUser && (
                  <TextInput
                    className="flex h-12 w-full rounded-md border-gray bg-gray py-2.5 pl-14 pr-4 placeholder:font-normal placeholder:text-gray-600 focus-within:border-purple sm:h-[60px] sm:pl-24"
                    data-testid="email"
                    disabled
                    id="email"
                    icon={
                      <div className="flex h-full w-10 items-center justify-center border-r border-gray-700 px-2 sm:w-[72px] sm:px-4">
                        <img src={SMS} alt="email" className="h-5 stroke-purple" />
                      </div>
                    }
                    inputStyles="bg-transparent text-white text-md sm:text-base disabled:bg-transparent"
                    placeholder="Email"
                    value={email}
                  />
                )}

                <Controller
                  name="password"
                  control={control}
                  rules={{
                    required: true,
                    validate: (value) => verifyPassword(value),
                  }}
                  render={({ field }) => (
                    <PasswordInput
                      id="password"
                      className="flex h-12 w-full rounded-md border-gray bg-gray py-2.5 pl-14 pr-4 placeholder:font-normal placeholder:text-gray-600 focus-within:border-purple sm:h-[60px] sm:pl-24"
                      icon={
                        <div className="flex h-full w-10 items-center justify-center border-r border-gray-700 px-2 sm:w-[72px] sm:px-4">
                          <img src={Shield} alt="email" className="h-5 stroke-purple" />
                        </div>
                      }
                      error={
                        errors.password && (errors.password.message || 'This field is required')
                      }
                      inputStyles="bg-transparent text-white text-md sm:text-base"
                      placeholder="Password"
                      renderIconStyles="fill-white"
                      autoComplete="new-password"
                      {...field}
                    />
                  )}
                />

                <Controller
                  name="confirmPassword"
                  control={control}
                  rules={{
                    required: true,
                    validate: (value, values) => {
                      const err = verifyPassword(value)
                      if (err) return err
                      if (value && values.password !== value) {
                        return 'Passwords do not match'
                      }
                      return undefined
                    },
                  }}
                  render={({ field }) => (
                    <PasswordInput
                      id="confirmPassword"
                      className="flex h-12 w-full rounded-md border-gray bg-gray py-2.5 pl-14 pr-4 placeholder:font-normal placeholder:text-gray-600 focus-within:border-purple sm:h-[60px] sm:pl-24"
                      icon={
                        <div className="flex h-full w-10 items-center justify-center border-r border-gray-700 px-2 sm:w-[72px] sm:px-4">
                          <img src={Shield} alt="email" className="h-5 stroke-purple" />
                        </div>
                      }
                      error={
                        errors.confirmPassword &&
                        (errors.confirmPassword.message || 'This field is required')
                      }
                      inputStyles="bg-transparent text-white text-md sm:text-base"
                      placeholder="Confirm Password"
                      renderIconStyles="fill-white"
                      autoComplete="new-password"
                      {...field}
                    />
                  )}
                />
              </div>

              {/* Submit */}
              <Button
                fullWidth
                className="text-md flex h-12 items-center rounded-md border-purple sm:h-[60px] sm:text-base"
                disabled={loading}
                label="Submit"
                loading={loading}
              />
            </form>
          )}
        </div>

        <div className="flex items-center justify-center gap-x-6 text-lg font-bold sm:justify-start">
          <img src={Fetch} alt="" className="h-[39px]" /> +{' '}
          <img src={Badger} alt="" className="h-[30px]" />
        </div>
      </div>
    </>
  )
}

export default PasswordReset
